import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { statisticsInitState, StatisticsInitType } from './state';
import { ProductOld } from 'src/admin-entity/interface/product/product-old.interface';
import { AppState } from 'src/store';
import { BASE_URL } from 'src/config/api';
import * as api from 'src/api/statistics/statistics';
import { dateFormatter } from 'src/utils/dateFormatter';

export const getSearchURL = (state: StatisticsInitType, baseUrl: string): string => {
    const {
        saleType,
        beginDate,
        endDate,
        dateType,
        category,
        selectedProducts,
        distributor,
        paymentMethodType,
        gradeList,
        taxStatus,
        page,
        take,
        discountType,
        searchType,
    } = state;

    console.log(baseUrl);
    const url = new URL(baseUrl);
    url.searchParams.append('beginDate', dateFormatter(new Date(beginDate), 'yyyy-MM-DD'));
    url.searchParams.append('endDate', dateFormatter(new Date(endDate), 'yyyy-MM-DD'));
    url.searchParams.append('page', `${page}`);
    url.searchParams.append('take', `${take}`);

    url.searchParams.append(`saleType`, saleType);
    url.searchParams.append(`searchType`, searchType);

    if (saleType === 'date') {
        url.searchParams.append('dateType', dateType);
    }

    if (selectedProducts.length > 0) {
        url.searchParams.append('productIds', selectedProducts.map(p => p.id).join('|'));
        url.searchParams.append('productNames', selectedProducts.map(p => p.name).join('|'));
    }
    if (category.id !== 0) {
        url.searchParams.append('categoryId', `${category.id}`);
        url.searchParams.append('categoryName', `${category.name}`);
    }
    if (distributor.id !== 0) {
        url.searchParams.append('distributorId', `${distributor.id}`);
        url.searchParams.append('distributorName', `${distributor.name}`);
    }
    if (paymentMethodType) {
        url.searchParams.append('paymentMethodType', paymentMethodType);
    }
    if (0 < gradeList.length && gradeList.length < 7) {
        url.searchParams.append('appUserGradeIds', gradeList.join('|'));
    }
    if (taxStatus) {
        url.searchParams.append('taxStatus', taxStatus);
    }
    if (0 < discountType.length && discountType.length < 3) {
        url.searchParams.append('paymentCategory', discountType.join('|'));
    }
    return url.toString();
};

export const searchStatistics = createAsyncThunk(
    `statisticsSlice/searchStatistics`,
    async (_, { getState, rejectWithValue }) => {
        try {
            const state = (getState() as AppState).statistics;
            const saleType = state.saleType;
            const url = getSearchURL(state, `${BASE_URL}/v1/admin/analytics/sales/${saleType}`);
            const res = await api.getStatistics(url);
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const getSumStatistics = createAsyncThunk(
    `statisticsSlice/getSumStatistics`,
    async (_, { getState, rejectWithValue }) => {
        try {
            const state = (getState() as AppState).statistics;
            const url = getSearchURL(state, `${BASE_URL}/v1/admin/analytics/sales/all`);
            const res = await api.getStatistics(url);
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const getStatisticsAll = createAsyncThunk(
    `statisticsSlice/getStatisticsAll`,
    async (_, { getState, rejectWithValue }) => {
        try {
            const state = (getState() as AppState).statistics;
            const saleType = state.saleType;
            const statisticsUrl = getSearchURL(state, `${BASE_URL}/v1/admin/analytics/sales/${saleType}`);
            const sumStatisticsUrl = getSearchURL(state, `${BASE_URL}/v1/admin/analytics/sales/all`);
            const [statisticsRes, sumStatisticsRes] = await Promise.all([
                api.getStatistics(statisticsUrl),
                api.getStatistics(sumStatisticsUrl),
            ]);
            return {
                statistics: statisticsRes.data,
                sumStatistics: sumStatisticsRes.data,
            };
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const getStatisticsExcel = createAsyncThunk(
    `statisticsSlice/getStatisticsExcel`,
    async (_, { getState, rejectWithValue }) => {
        try {
            const state = (getState() as AppState).statistics;

            let type = state.searchType;
            if (type === 'payment-method-type') {
                type = 'payment_method_type';
            }
            if (type === 'app-user-grade') {
                type = 'grade';
            }
            const url = getSearchURL(state, `${BASE_URL}/v1/admin/analytics/sales/excel?type=${type.toUpperCase()}`);
            const res = await api.getStatisticsExcel(url.toString());
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const getUserDataExcel = createAsyncThunk(
    `statisticsSlice/getUserDataExcel`,
    async (ids: any[], { getState, rejectWithValue }) => {
        try {
            const state = (getState() as AppState).statistics;
            let type = state.searchType;
            if (type === 'payment-method-type') {
                type = 'payment_method_type';
            }
            if (type === 'app-user-grade') {
                type = 'grade';
            }
            const url = getSearchURL(
                state,
                `${BASE_URL}/v1/admin/analytics/sales/excel-users?type=${type.toUpperCase()}`,
            );
            const res = await api.getUserDataExcel(url.toString(), ids);
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const statisticsSlice = createSlice({
    name: 'statistics',
    initialState: statisticsInitState,
    reducers: {
        reset: state => {
            return {
                ...statisticsInitState,
                saleType: state.saleType,
                searchType: state.searchType,
            };
        },
        urlUpdate: state => {
            const url = getSearchURL(state, `${window.location.origin}/statistics`);
            window.history.pushState({}, '', url);
            // Dispatch a popstate event to ensure React Router detects the URL change
            window.dispatchEvent(new PopStateEvent('popstate'));
        },
        setSearchValues: (state, action: PayloadAction<string>) => {
            const params = new URLSearchParams(action.payload);
            state.beginDate = params.get(`beginDate`);
            state.endDate = params.get(`endDate`);
            state.page = +params.get('page');
            state.take = +params.get('take');
            state.saleType = params.get('saleType');
            state.searchType = params.get('searchType');
            if (params.get('dateType')) {
                state.dateType = params.get('dateType');
            } else {
                state.dateType = 'day';
            }
            if (params.get('productIds')) {
                const ids = params.get('productIds').split('|');
                const names = params.get('productNames').split('|');
                state.selectedProducts = ids.map((id, index) => {
                    return {
                        id: +id,
                        name: names[index],
                    };
                });
            } else {
                state.selectedProducts = [];
            }
            if (params.get('categoryId')) {
                state.category = {
                    id: +params.get('categoryId'),
                    name: params.get('categoryName'),
                };
            } else {
                state.category = {
                    id: 0,
                    name: '',
                };
            }
            if (params.get('distributorId')) {
                state.distributor = {
                    id: +params.get('distributorId'),
                    name: params.get('distributorName'),
                };
            } else {
                state.distributor = {
                    id: 0,
                    name: '',
                };
            }
            if (params.get('paymentMethodType')) {
                state.paymentMethodType = params.get('paymentMethodType');
            } else {
                state.paymentMethodType = '';
            }
            if (params.get('appUserGradeIds')) {
                state.gradeList = params.get('appUserGradeIds').split('|');
            } else {
                state.gradeList = [];
            }
            if (params.get(`taxStatus`)) {
                state.taxStatus = params.get(`taxStatus`);
            } else {
                state.taxStatus = '';
            }
            if (params.get(`paymentCategory`)) {
                state.discountType = params.get(`paymentCategory`).split('|');
            } else {
                state.discountType = [];
            }
        },
        updateSaleType: (state, action: PayloadAction<string>) => {
            state.saleType = action.payload;
        },
        updateDateType: (state, action: PayloadAction<string>) => {
            state.dateType = action.payload;
        },
        updateBeginDate: (state, action: PayloadAction<string>) => {
            state.beginDate = action.payload;
        },
        updateEndDate: (state, action: PayloadAction<string>) => {
            state.endDate = action.payload;
        },
        updateSelectedProducts: (state, action: PayloadAction<ProductOld[]>) => {
            state.selectedProducts = action.payload;
        },
        removeProductInSelectedProducts: (state, action: PayloadAction<ProductOld>) => {
            state.selectedProducts = state.selectedProducts.filter(p => p.id !== action.payload.id);
        },
        removeAllProductInSelectedProducts: state => {
            state.selectedProducts = [];
        },
        updateCategory: (state, action: PayloadAction<{ id: number; name: string }>) => {
            state.category = action.payload;
        },
        updateDistributor: (state, action: PayloadAction<{ id: number; name: string }>) => {
            state.distributor = action.payload;
        },
        updatePaymentMethodType: (state, action: PayloadAction<string>) => {
            state.paymentMethodType = action.payload;
        },
        updateGradeList: (state, action: PayloadAction<string[]>) => {
            state.gradeList = action.payload;
        },
        updateDiscountType: (state, action: PayloadAction<string[]>) => {
            state.discountType = action.payload;
        },
        updateTaxStatus: (state, action: PayloadAction<string>) => {
            state.taxStatus = action.payload;
        },
        updatePage: (state, action: PayloadAction<number>) => {
            state.page = action.payload;
        },
        updateTake: (state, action: PayloadAction<number>) => {
            state.take = action.payload;
        },
    },
    extraReducers: builder => {
        builder.addCase(searchStatistics.fulfilled, (state, { payload }) => {
            payload.list = payload.list.map((i, index) => {
                return {
                    ...i,
                    id: index,
                };
            });
            state.searchResult = payload;
            state.searchType = state.saleType;
        });
        builder.addCase(getStatisticsAll.fulfilled, (state, { payload }) => {
            payload.statistics.list = payload.statistics.list.map((i, index) => {
                return {
                    ...i,
                    id: index,
                };
            });
            state.searchResult = payload.statistics;
            state.sumStatistics = payload.sumStatistics;
            state.searchType = state.saleType;
        });
    },
});

export const {
    reset,
    urlUpdate,
    setSearchValues,
    updateSaleType,
    updateDateType,
    updateBeginDate,
    updateEndDate,
    updateSelectedProducts,
    removeProductInSelectedProducts,
    removeAllProductInSelectedProducts,
    updateCategory,
    updateDistributor,
    updatePaymentMethodType,
    updateGradeList,
    updateDiscountType,
    updateTaxStatus,
    updatePage,
    updateTake,
} = statisticsSlice.actions;
export default statisticsSlice.reducer;
